
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, Send, User, Edit2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { useAuthSession } from '@/hooks/use-auth-session';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  user?: {
    username?: string;
    avatar_url?: string;
  };
}

interface PlaceCommentsProps {
  comments: Comment[];
  isLoggedIn: boolean;
  onCommentSubmit: (comment: string) => void;
  onCommentUpdate: (commentId: string, content: string) => void;
  onCommentDelete: (commentId: string) => void;
  isSubmitting?: boolean;
}

export function PlaceComments({
  comments,
  isLoggedIn,
  onCommentSubmit,
  onCommentUpdate,
  onCommentDelete,
  isSubmitting
}: PlaceCommentsProps) {
  const [comment, setComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const session = useAuthSession();
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    onCommentSubmit(comment);
    setComment('');
  };

  const handleUpdate = (commentId: string) => {
    if (!editContent.trim()) return;
    onCommentUpdate(commentId, editContent);
    setEditingCommentId(null);
    setEditContent('');
  };

  const startEditing = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold flex items-center gap-2">
        <MessageCircle className="w-6 h-6 text-purple-600" />
        Comments
      </h2>

      {isLoggedIn ? (
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <Textarea
                  placeholder="Write your comment..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    disabled={!comment.trim() || isSubmitting}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Post Comment
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">
              Sign in to comment on this place
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {comments?.map((comment) => (
          <Card key={comment.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <Avatar>
                  <AvatarImage src={comment.user?.avatar_url || ''} />
                  <AvatarFallback>
                    <User className="w-4 h-4" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{comment.user?.username || 'Anonymous'}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(comment.created_at), 'PPp')}
                      </p>
                    </div>
                    {session?.user?.id === comment.user_id && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEditing(comment)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCommentDelete(comment.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editingCommentId === comment.id ? (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="min-h-[100px]"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditing}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(comment.id)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2">{comment.content}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {comments?.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            No comments yet. Be the first to comment!
          </p>
        )}
      </div>
    </div>
  );
}
